/* eslint-disable @typescript-eslint/naming-convention */
// We want these to match the API schema as closely as possible

import type { JSONSchema7 } from 'json-schema';

import type { DeepReadonly } from './types';

export const ai21LabsJsonSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    tools: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          type: {
            type: 'string',
            enum: ['function'],
            description: "The type of tool. Currently the only supported value is 'function'.",
          },
          function: {
            type: 'object',
            properties: {
              name: {
                type: 'string',
                description: 'The name of the function.',
              },
              description: {
                type: 'string',
                description: 'A complete description of what the function does, what it returns, and any limitations.',
              },
              parameters: {
                type: 'object',
                properties: {
                  PARAMETER_NAME: {
                    type: 'object',
                    properties: {
                      type: {
                        type: 'string',
                        enum: ['string', 'integer', 'float', 'array', 'boolean', 'enum'],
                        description: 'The type of the parameter.',
                      },
                      enum: {
                        type: 'array',
                        items: {
                          type: 'string',
                        },
                        description: 'The list of possible values for an enum-type parameter.',
                      },
                      description: {
                        type: 'string',
                        description: 'A description of the parameter.',
                      },
                    },
                    required: ['type', 'description'],
                  },
                },
                required: ['PARAMETER_NAME'],
              },
              required: {
                type: 'array',
                items: {
                  type: 'string',
                },
                description: 'A list of required parameters.',
              },
            },
            required: ['name', 'description', 'parameters'],
          },
        },
        required: ['type', 'function'],
      },
    },
    response_format: {
      type: 'object',
      properties: {
        type: {
          type: 'string',
          enum: ['json_object'],
          description:
            "If left blank, will be text. If set to {'type': 'json_object'}, it will try to return the entire response in valid JSON format.",
        },
      },
    },
    documents: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          content: {
            type: 'string',
            description: 'The content of the document.',
          },
          metadata: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                key: {
                  type: 'string',
                  description: "The type of metadata (e.g., 'author', 'date', 'url').",
                },
                value: {
                  type: 'string',
                  description: 'The value of the metadata.',
                },
              },
              required: ['key', 'value'],
            },
            description: 'Metadata about the document.',
          },
        },
        required: ['content'],
      },
    },
    max_tokens: {
      type: 'integer',
      description: 'The maximum number of tokens to allow for each generated response message.',
      default: 4096,
      minimum: 0,
      maximum: 4096,
    },
    temperature: {
      type: 'number',
      description: 'How much variation to provide in each answer. Default: 0.4.',
      default: 0.4,
      minimum: 0,
      maximum: 2.0,
    },
    top_p: {
      type: 'number',
      description:
        'Limit the pool of next tokens in each step to the top N percentile of possible tokens. Default: 1.0.',
      default: 1.0,
      minimum: 0,
      maximum: 1.0,
    },
    stop: {
      type: ['string', 'array'],
      items: {
        type: 'string',
      },
      description:
        'End the message when the model generates one of these strings. The stop sequence is not included in the generated message.',
    },
    n: {
      type: 'integer',
      description: 'How many chat responses to generate. Default: 1.',
      default: 1,
      minimum: 1,
      maximum: 16,
    },
    stream: {
      type: 'boolean',
      description: 'Whether or not to stream the result one token at a time using server-sent events. Default: False.',
    },
  },
  additionalProperties: false,
} as const;

// this confirms that the type is a valid JSON schema, but we want it as a `const` so we can use it to index the properties
export const ai21LabsSchemaProperties: DeepReadonly<JSONSchema7> = ai21LabsJsonSchema;

export const retrieveAi21LabsParameters = (): string[] => Object.keys(ai21LabsJsonSchema.properties);
